import { gql } from '@apollo/client';

// GraphQL
import { client } from '../../../../../../gql/client';
import {
  GET_INTERLOCUTOR_GROUP_BY_ID,
  GET_MEDIA_OF_COMMUNICATION_BY_ID,
} from '../../../../../../gql/queries/communication';
import {
  GET_CAPITOL_BY_ID,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOL_BY_ID,
} from '../../../../../../gql/queries/orders/predefinedData';
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
} from '../../../../../../gql/queries/geoPositions/roads';

// Types
import { PassDataParams } from './types';
import { laneLabel, trackLabel } from '../../../../../../utils/catalogLabels';
import { GET_TEAM_BY_ID } from '../../../../../../gql/queries/users';

export const passData = async ({
  formData,
  storedDataOTSelected,
}: PassDataParams) => {
  const operator = formData.get('operator');
  const registerInitDate = formData.get('registerInitDate');
  const groupInterId = formData.get('groupInter');
  const mediaId = formData.get('media');
  const sendStatus = formData.get('sendStatus');
  const concessionId = formData.get('concession');
  const roadId = formData.get('road');
  const classificationId = formData.get('classification');
  const capitolId = formData.get('capitol');
  const subCapitolId = formData.get('subCapitol');

  // ✅ M2M: Handle team or teams (backward compatible)
  const teamIdRaw = formData.get('team') || formData.get('teams');
  const teamIds: string[] = !teamIdRaw
    ? []
    : typeof teamIdRaw === 'string'
      ? teamIdRaw.split(',').filter(Boolean)
      : Array.isArray(teamIdRaw)
        ? (teamIdRaw.filter((item): item is string => typeof item === 'string'))
        : (typeof teamIdRaw === 'string' ? [teamIdRaw] : []);

  const editCommunication = formData.get('editCommunication');
  const dataDiff = formData.get('diff');
  const roid = formData.get('roid');
  const parentOrderId = formData.get('parentOrderId');

  const [
    {
      data: { concession },
    },
    {
      data: { road },
    },
    {
      data: { interlocutorGroup },
    },
    {
      data: { mediaOfCommunication },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CONCESSION_BY_ID,
      variables: {
        id: concessionId,
      },
    }),
    client.query({
      query: GET_ROAD_BY_ID,
      variables: {
        id: roadId,
      },
    }),
    client.query({
      query: GET_INTERLOCUTOR_GROUP_BY_ID,
      variables: {
        id: groupInterId,
      },
    }),
    client.query({
      query: GET_MEDIA_OF_COMMUNICATION_BY_ID,
      variables: {
        id: mediaId,
      },
    }),
  ]);

  // ✅ M2M: Fetch ALL teams, not just one
  let teamValues: any[] = [];
  if (teamIds.length > 0) {
    const teamQueries = teamIds.map((teamId: string) =>
      client.query({
        query: GET_TEAM_BY_ID,
        variables: { id: teamId },
      })
    );
    const teamResults = await Promise.all(teamQueries);
    teamValues = teamResults.map((result) => result.data.team);
  }

  const [
    {
      data: { classification },
    },
    {
      data: { capitol },
    },
    {
      data: { subCapitol },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CLASSIFICATION_BY_ID_OR_ROID,
      variables: {
        id: classificationId,
      },
    }),
    client.query({
      query: GET_CAPITOL_BY_ID,
      variables: {
        id: capitolId,
      },
    }),
    client.query({
      query: GET_SUB_CAPITOL_BY_ID,
      variables: {
        id: subCapitolId,
      },
    }),
  ]);
  const classificationPass = classification.name;
  const capitolPass = capitol.name;
  const subCapitolPass = subCapitol.name;

  // T36: Resolve predefinedOrder name from Apollo cache
  let predefinePass;
  const predefinedOrderId = formData.get('predefine');
  if (predefinedOrderId) {
    const predefinedOrderData = client.readFragment({
      id: `PredefinedOrder:${predefinedOrderId}`,
      fragment: gql`
        fragment PredefinedOrderNameEdit on PredefinedOrder {
          id
          name
        }
      `,
    });
    predefinePass = predefinedOrderData?.name;
  }

  const city = formData.get('city');
  const pkInitRoad = formData.get('pkInitRoad');
  const pkEndRoad = formData.get('pkEndRoad');
  const trackValue = formData.get('track');
  const direction = formData.get('direction');
  const margin = formData.get('margin');
  const laneValue = formData.get('lane');
  const pkInit = formData.get('pkInit');
  const pkEnd = formData.get('pkEnd');
  const description = formData.get('description');
  const notes = formData.get('notes');
  const specialTag = formData.get('specialTag');

  return {
    operator,
    registerInitDate,
    groupInter: interlocutorGroup?.name,
    media: mediaOfCommunication?.name,
    sendStatus,
    concession: concession?.name,
    city,
    pkInitRoad,
    pkEndRoad,
    road: road?.name,
    track: trackLabel(trackValue as string | null),
    direction,
    margin,
    lane: laneLabel(laneValue as string | null),
    pkInit,
    pkEnd,
    roadDirections: road.directions,
    description,
    notes,
    specialTag,
    classification: classificationPass,
    capitol: capitolPass,
    subCapitol: subCapitolPass,
    predefine: predefinePass,
    teams: teamValues.map((t) => t.name), // ✅ M2M: Return array of team names
    storedDataOTSelected,
    editCommunication,
    dataDiff: JSON.parse(dataDiff as string),
    roid: JSON.parse(roid as string),
    parentOrderId,
  };
};
